import {
  Alert as MuiAlert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle, ErrorOutline, Lock, Sell, Settings, WarningAmber } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import * as admin from '@/api/admin';
import type { PlanTier, ServiceHealthStatus } from '@/api/admin';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { PadronMasterCertCard } from '@/components/admin/PadronMasterCertCard';
import {
  COMMERCIAL_PRODUCT_LABEL,
  commercialProductFromTechnical,
  type CommercialProductCode,
} from '@/lib/commercialProducts';
import { formatFeatureFlagLabel } from '@/lib/plans';

export const Route = createFileRoute('/_authenticated/admin/configuracion/')({
  component: ConfiguracionPage,
});

const COMMERCIAL_PRODUCT_ORDER: CommercialProductCode[] = [
  'Monotributo',
  'ResponsableInscripto',
  'EstudioContable',
  'ApiProgramadores',
  'Empresa',
];

const HEALTH: Record<ServiceHealthStatus, { label: string; color: 'success' | 'warning' | 'error'; icon: JSX.Element }> = {
  Operational: { label: 'Operativo', color: 'success', icon: <CheckCircle color="success" fontSize="small" /> },
  Degraded: { label: 'Degradado', color: 'warning', icon: <WarningAmber color="warning" fontSize="small" /> },
  Outage: { label: 'Caído', color: 'error', icon: <ErrorOutline color="error" fontSize="small" /> },
};

function ConfiguracionPage(): JSX.Element {
  const configQ = useQuery({ queryKey: ['platform-config'], queryFn: admin.getPlatformConfig });
  const healthQ = useQuery({ queryKey: ['service-health'], queryFn: admin.getServiceHealth });

  if (configQ.isLoading) return <LoadingScreen message="Cargando configuración…" />;
  if (configQ.error) return <MuiAlert severity="error">{toApiError(configQ.error).message}</MuiAlert>;

  const config = configQ.data!;
  const byCommercialProduct = groupPlanTiersByCommercialProduct(config.plans);

  return (
    <Box>
      <Stack sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Settings color="primary" /> Configuración
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Catálogo comercial, integraciones y seguridad de la plataforma (solo lectura).
        </Typography>
      </Stack>

      {/* Integraciones (reusa el estado de servicios) */}
      <Typography variant="h6" sx={{ mb: 1 }}>Integraciones</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {(healthQ.data?.services ?? []).map((s) => (
          <Grid item xs={6} sm={4} md={3} key={s.service}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                {HEALTH[s.status].icon}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.service}</Typography>
                  <Typography variant="caption" color="text.secondary">{HEALTH[s.status].label}</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
        {healthQ.isLoading && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Cargando estado de integraciones…</Typography></Grid>}
      </Grid>

      {/* Certificado maestro de consultas (padrón) */}
      <Typography variant="h6" sx={{ mb: 1 }}>Certificados de plataforma</Typography>
      <Box sx={{ mb: 4 }}>
        <PadronMasterCertCard />
      </Box>

      {/* Catálogo comercial & compatibilidad */}
      <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Sell fontSize="small" /> Catálogo por producto comercial
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {byCommercialProduct.map(({ code, label, tiers }) => (
          <Grid item xs={12} md={4} key={code}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{label}</Typography>
                <Stack spacing={1.5}>
                  {tiers.map((t) => (
                    <Box key={t.code}>
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.displayName}</Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>
                          {t.contactSales ? 'Contactar' : t.priceUsd === 0 ? 'Gratis' : `USD ${t.priceUsd}/mes`}
                        </Typography>
                      </Stack>
                      {code === 'Empresa' && (
                        <Typography variant="caption" color="text.secondary">
                          Producto pendiente de clasificación comercial
                        </Typography>
                      )}
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                        {t.features.slice(0, 4).map((f) => (
                          <Chip key={f} size="small" variant="outlined" label={formatFeatureFlagLabel(f)} />
                        ))}
                        {t.features.length > 4 && <Chip size="small" variant="outlined" label={`+${t.features.length - 4}`} />}
                      </Stack>
                      <Divider sx={{ mt: 1.5 }} />
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Seguridad */}
      <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Lock fontSize="small" /> Seguridad
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <SecurityRow ok={config.security.twoFactorRequiredForAdmin} label="2FA obligatorio para Platform Admin" />
          <SecurityRow ok={config.security.impersonationAudited} label="Impersonación 100% auditada" />
          <SecurityRow ok={config.security.secretsInKeyVault} label="Secretos en Key Vault (no en código)" />
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircle color="success" fontSize="small" />
            <Typography variant="body2">Ambientes AFIP: {config.security.afipEnvironments.join(' · ')}</Typography>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

export function commercialProductForConfigTier(tier: PlanTier): CommercialProductCode {
  return commercialProductFromTechnical(
    tier.product as 'Empresa' | 'Estudio' | 'Api',
    tier.code,
    tier.commercialProduct,
  );
}

export function groupPlanTiersByCommercialProduct(
  tiers: PlanTier[],
): Array<{ code: CommercialProductCode; label: string; tiers: PlanTier[] }> {
  return COMMERCIAL_PRODUCT_ORDER
    .map((code) => {
      const groupedTiers = tiers.filter((tier) => commercialProductForConfigTier(tier) === code);
      const backendLabel = groupedTiers.find((tier) => tier.commercialProductName)?.commercialProductName;
      return {
        code,
        label: code === 'Empresa' ? COMMERCIAL_PRODUCT_LABEL.Empresa : backendLabel ?? COMMERCIAL_PRODUCT_LABEL[code],
        tiers: groupedTiers,
      };
    })
    .filter((group) => group.tiers.length > 0);
}

function SecurityRow({ ok, label }: { ok: boolean; label: string }): JSX.Element {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {ok ? <CheckCircle color="success" fontSize="small" /> : <ErrorOutline color="error" fontSize="small" />}
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}
